/**
 * Connects a control to its surrounding `<Field>`: the control gets the field's `id`
 * (for the `<label htmlFor>`), `aria-describedby` (help + error), `aria-invalid` and
 * `aria-required`.
 */
import { createContext, useContext } from 'react';

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldControlProps {
  id?: string;
  'aria-describedby'?: string;
  /** Overrides the field's invalid state. */
  invalid?: boolean;
}

/** Accessibility props for a control, merging its own props with the field's. */
export function useFieldControl(own: FieldControlProps) {
  const field = useContext(FieldContext);
  const describedBy = [field?.describedBy, own['aria-describedby']].filter(Boolean).join(' ');
  return {
    id: own.id ?? field?.id,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': (own.invalid ?? field?.invalid) || undefined,
    'aria-required': field?.required || undefined,
  };
}
